import { useMemo } from "react";
import clientsBg from "@/assets/clients-bg.png";

// Logo imports
import logoEdutecno from "@/assets/logos/edutecno.png";
import logoDuocUc from "@/assets/logos/duoc-uc.png";
import logoLaPintana from "@/assets/logos/la-pintana.png";
import logoAbcdin from "@/assets/logos/abcdin.png";
import logoNuevoPudahuel from "@/assets/logos/nuevo-pudahuel.jpg";
import logoCegelec from "@/assets/logos/cegelec.png";
import logoClaroArena from "@/assets/logos/claro-arena.webp";
import logoCopec from "@/assets/logos/copec.png";
import logoDecathlon from "@/assets/logos/decathlon.png";
import logoFamilyShop from "@/assets/logos/family-shop.png";
import logoKomatsu from "@/assets/logos/komatsu.png";
import logoMallPlaza from "@/assets/logos/mall-plaza.png";
import logoMapfre from "@/assets/logos/mapfre.png";
import logoUbb from "@/assets/logos/ubb.png";
import logoUcChristus from "@/assets/logos/ucchristus.png";
import logoUnimarc from "@/assets/logos/unimarc.png";
import logoUpita from "@/assets/logos/upita.png";
import logoGeoworking from "@/assets/logos/geoworking.jpg";
import logoSalaPrensaByn from "@/assets/logos/sala-prensa-byn.png";

interface Client {
  name: string;
  logo?: string;
}

const clients: Client[] = [
  { name: "CLARO ARENA", logo: logoClaroArena },
  { name: "KOMATSU", logo: logoKomatsu },
  { name: "DECATHLON", logo: logoDecathlon },
  { name: "UPITA", logo: logoUpita },
  { name: "ABCDIN", logo: logoAbcdin },
  { name: "DUOC UC", logo: logoDuocUc },
  { name: "UBB", logo: logoUbb },
  { name: "MUN. LA PINTANA", logo: logoLaPintana },
  { name: "NUEVO PUDAHUEL", logo: logoNuevoPudahuel },
  { name: "FAMILY SHOP", logo: logoFamilyShop },
  { name: "MALL PLAZA", logo: logoMallPlaza },
  { name: "SALA DE PRENSA B&N", logo: logoSalaPrensaByn },
  { name: "MAPFRE", logo: logoMapfre },
  { name: "Cegelec", logo: logoCegelec },
  { name: "UNIMARC", logo: logoUnimarc },
  { name: "EDUTECNO", logo: logoEdutecno },
  { name: "GEOWORKING", logo: logoGeoworking },
  { name: "COPEC", logo: logoCopec },
  { name: "UC CHRISTUS", logo: logoUcChristus }
];

// Fisher-Yates shuffle algorithm
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const Clients = () => {
  // Randomize clients on component mount
  const shuffledClients = useMemo(() => shuffleArray(clients), []);

  const renderClientCard = (client: Client, keyPrefix: string, index: number) => (
    <div 
      key={`${keyPrefix}-${index}`}
      className="flex-shrink-0 flex items-center justify-center p-6 rounded-2xl border border-white/30 hover-liquid group cursor-pointer shadow-lg min-w-[180px] h-[100px] bg-white"
    >
      {client.logo ? (
        <img 
          src={client.logo} 
          alt={client.name}
          className="max-h-16 max-w-[150px] object-contain group-hover:scale-110 transition-transform duration-500"
        />
      ) : (
        <span className="text-lg font-montserrat font-bold text-foreground/70 group-hover:text-primary transition-colors duration-500 whitespace-nowrap">
          {client.name}
        </span>
      )}
    </div>
  );

  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background Image - Centered */}
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${clientsBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
        }}
      />
      
      {/* Subtle overlay for text contrast */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-white/40" />
      
      {/* Shimmer effect */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          background: 'linear-gradient(110deg, transparent 25%, rgba(255, 255, 255, 0.5) 50%, transparent 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 10s linear infinite',
        }}
      />
      
      {/* Contenido */}
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-4xl md:text-5xl font-montserrat font-bold mb-6 text-balance text-white relative inline-block">
            <span 
              className="absolute inset-0 blur-2xl opacity-70 -z-10"
              style={{ background: 'radial-gradient(circle, rgba(255, 107, 53, 0.8) 0%, rgba(255, 107, 53, 0.4) 50%, transparent 70%)' }}
            />
            TRABAJAMOS CON
          </h2>
          <p className="text-sm md:text-lg text-white/80 max-w-3xl mx-auto font-inter leading-relaxed md:whitespace-nowrap">
            Empresas e Instituciones que confían en nuestra experiencia y profesionalismo
          </p>
        </div>

        {/* Carrusel Infinito CSS con mask fade */}
        <div 
          className="relative w-full overflow-hidden"
          style={{
            maskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)'
          }}
        >
          <div 
            className="flex gap-6 py-4 hover:[animation-play-state:paused]"
            style={{
              animation: 'scroll-left 40s linear infinite',
              width: 'max-content'
            }}
          >
            {/* Primera ronda de logos */}
            {shuffledClients.map((client, index) => renderClientCard(client, 'first', index))}
            {/* Segunda ronda (duplicado para loop infinito seamless) */}
            {shuffledClients.map((client, index) => renderClientCard(client, 'second', index))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Clients;
